'use client'

import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSocket } from '@/providers/SocketContext'
import { emitCommand } from '@/lib/socket-command'
import { parseChannelCounters, parseChannelCountersList } from '@/lib/channelCounters'
import { channelsKeys } from '@/hooks/useChannelsQuery'
import type { ChannelCountersEvent } from '@/types/SocketEvents'
import type { ChannelWithStats } from '@/types/channel'

/**
 * Aplica contadores ABSOLUTOS sobre a lista já conhecida.
 *
 * - Canal ausente do cache é **ignorado**: socket não é fonte de existência de canal
 *   (quem acrescenta canal é o HTTP, em `mergeChannelStats`).
 * - Cache ainda `undefined` devolve `undefined` — nunca inventa a lista.
 * - Nada mudou ⇒ **mesma referência**. `update_kanban_state` publica `channel_update`
 *   sem mexer em contador nenhum, então o `counters_update` repetido é caso corrente;
 *   referência nova ali re-renderizaria o rail a cada arrasto de card no kanban.
 */
export function applyCounters(
  current: ChannelWithStats[] | undefined,
  deltas: ChannelCountersEvent[],
): ChannelWithStats[] | undefined {
  if (!current || deltas.length === 0) return current

  const byId = new Map(deltas.map((delta) => [delta.channel_id, delta]))
  let mudou = false

  const next = current.map((channel) => {
    const delta = byId.get(channel.db_id)
    if (!delta) return channel
    if (
      channel.chats_count === delta.chats_count &&
      channel.waiting_chats === delta.waiting_chats &&
      channel.total_unread_messages === delta.total_unread_messages
    ) {
      return channel
    }
    mudou = true
    return {
      ...channel,
      chats_count: delta.chats_count,
      waiting_chats: delta.waiting_chats,
      total_unread_messages: delta.total_unread_messages,
    }
  })

  return mudou ? next : current
}

/**
 * Mantém `['channels']` vivo pelos contadores do socket. Montado UMA vez, app-wide,
 * em `PrivateProviders`: o badge do rail vale em `/admin`, `/conta` e `/notifications`,
 * onde o `ChatBotProvider` nem existe (`useChatBot()` lança fora de `/chatbot/**`).
 *
 * `setQueryData`, nunca `invalidateQueries` — invalidar a cada mensagem devolveria o
 * request que esta feature veio eliminar. O `refetchInterval: 30_000` continua sendo o
 * piso de resync e não deve ser reduzido nem removido.
 */
export function useChannelCountersLive(): void {
  const { socket } = useSocket()
  const queryClient = useQueryClient()

  // Guarda SÓ em `socket` (não `socketReady`) — mesma razão do `useReadReceiptsLive`:
  // no disconnect o cleanup removeria os listeners e a reconexão emite `connect`
  // SÍNCRONO antes de o re-render re-registrar, então o `enter_counters` da volta
  // nunca sairia. Registrar num socket desconectado é inofensivo (nada chega).
  useEffect(() => {
    if (!socket) return

    const patch = (deltas: ChannelCountersEvent[]) => {
      if (deltas.length === 0) return
      const current = queryClient.getQueryData<ChannelWithStats[]>(channelsKeys.all)
      const next = applyCounters(current, deltas)
      // Mesma referência = nada mudou. Chamar `setQueryData` mesmo assim bumparia
      // `dataUpdatedAt`, que faz parte do resultado do observer, e re-renderizaria
      // todo consumidor de `useChannelsQuery` à toa.
      if (next === current) return
      queryClient.setQueryData<ChannelWithStats[]>(channelsKeys.all, next)
    }

    const join = () => {
      // Veredito ignorado de propósito (spec §6): `denied` é silêncio — não há tela
      // dedicada a poluir com toast e o número segue vindo do HTTP; `unknown` (ack
      // não voltou em 10s) NÃO é negativa; e `session_expired` já recicla a conexão
      // em `lib/socket`, cuja volta cai neste mesmo handler de `connect`.
      void emitCommand(socket, 'enter_counters')
    }

    const onSnapshot = (raw: unknown) => patch(parseChannelCountersList(raw))
    const onUpdate = (raw: unknown) => {
      const delta = parseChannelCounters(raw)
      if (delta) patch([delta])
    }

    socket.on('counters_info', onSnapshot)
    socket.on('counters_update', onUpdate)
    socket.on('connect', join)
    // Socket já conectado quando o hook monta (o `connect` veio do login, antes deste
    // effect): o listener sozinho nunca dispararia.
    if (socket.connected) join()

    return () => {
      socket.off('counters_info', onSnapshot)
      socket.off('counters_update', onUpdate)
      socket.off('connect', join)
      socket.emit('leave_counters')
    }
  }, [socket, queryClient])
}
